// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type DepositAmount,
  type OptimizationObjective,
  type OptimizationPayload,
  type PctLosses,
  type PortfolioBacktest,
  type PortfolioValidationResp,
} from '@shared/data-access/interfaces/api/ai/portfolio';
import { type OptimizationResponse } from '@shared/data-access/interfaces/api/ai/target-strategy';
import { type PortfolioAnalysis } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  portfolioStrategyPayload,
  type PortfolioStrategy,
} from '@shared/data-access/interfaces/api/ai/portfolio-strategy';
import { type EsgAnalysis } from '@shared/data-access/interfaces/api/ai/esg-analysis';
import { type TheoreticalTrade } from '@shared/data-access/interfaces/api/ai/theoretical-trade';
import {
  type RebalanceReportingTarget,
  type PortfolioRebalanceStrategy,
  type PortfolioRebalanceTrading,
  type PortfolioRebalanceGeneral,
  type RebalanceTrade,
  type RebalanceReportingTargetResp,
  type RebalanceReportingTaxes,
  type RebalanceReportingTaxesResp,
} from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';
import {
  type QueryReturnValue,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import {
  rebalanceDepositPayload,
  rebalanceGeneralPayload,
  rebalanceTradingPayload,
  rebalanceWithdrawalsPayload,
  reportingTargetPayload,
  reportingTaxesPayload,
} from '@shared/data-access/utils/payloads/ai';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import { assetsApi } from './assets-api';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const portfolioApi = aiApi.injectEndpoints({
  endpoints: build => ({
    portfolioBacktest: build.query<
      PortfolioBacktest,
      { params: { hist_w: number }; payload: PortfolioStrategy; assetType?: string }
    >({
      query: ({ payload, params, assetType = 'stock' }) => ({
        url: `/portfolio/${assetType}/backtest/`,
        method: 'POST',
        body: cleanPayload(payload, portfolioStrategyPayload),
        params,
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Portfolio', id: 'Backtest' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    portfolioAnalysis: build.query<
      PortfolioAnalysis,
      { params: { hist_w: number }; payload: PortfolioStrategy; assetType?: string }
    >({
      query: ({ payload, params, assetType = 'stock' }) => ({
        url: `/portfolio/${assetType}/analysis/`,
        method: 'POST',
        body: cleanPayload(payload, portfolioStrategyPayload),
        params,
      }),
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'Portfolio', id: 'Analysis' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    portfolioStockAnalysisEsg: build.query<
      EsgAnalysis,
      { payload: PortfolioStrategy }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/stock/analysis/esg/',
        method: 'POST',
        body: cleanPayload(payload, portfolioStrategyPayload),
      }),
      providesTags: [{ type: 'Portfolio', id: 'Esg' }],
    }),

    portfolioOptimization: build.query<
      OptimizationResponse,
      {
        params: { hist_w: number; opt_global: string };
        payload: OptimizationPayload;
        assetType?: string;
      }
    >({
      query: ({ payload, params, assetType = 'stock' }) => ({
        url: `/portfolio/${assetType}/optimization/`,
        method: 'POST',
        body: payload,
        params,
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'Optimization' }],
    }),

    portfolioValidation: build.query<
      PortfolioValidationResp,
      { payload: PortfolioStrategy; assetType?: string }
    >({
      queryFn: async (
        { payload, assetType = 'stock' },
        _api,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const response = (await fetchWithBQ({
          url: `/portfolio/${assetType}/validation/`,
          method: 'POST',
          body: cleanPayload(payload, portfolioStrategyPayload),
        })) as QueryReturnValue<
          PortfolioValidationResp,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error) return { error: response.error as FetchBaseQueryError };

        const isins = response.data.unsupported_ticker_positions.filter(
          ticker => ticker.length >= 12,
        );

        const assetsTypes: Record<string, string> = {
          stock: 'stocks',
          etf: 'etfs',
          mutual_fund: 'mutual_funds',
        };

        const searchResults = await Promise.all(
          isins.map(isin =>
            _api
              .dispatch(
                assetsApi.endpoints.search.initiate({
                  ticker: isin,
                  name: isin,
                  assetType: assetsTypes[assetType],
                  key_search: isin,
                  isin,
                  details: false,
                }),
              )
              .unwrap(),
          ),
        );

        const data: PortfolioValidationResp = {
          ...response.data,
          unsupported_ticker_positions:
            response.data.unsupported_ticker_positions.filter(
              ticker =>
                !searchResults.find(searchResult =>
                  searchResult.find(search => search.isin === ticker),
                ),
            ),
        };

        return {
          meta: response.meta,
          data: data,
        };
      },
      extraOptions: {},
      providesTags: [{ type: 'Portfolio', id: 'Validation' }],
    }),

    taxesTaxHarvesting: build.query<
      TheoreticalTrade[],
      { payload: PortfolioStrategy; params: PctLosses }
    >({
      query: ({ payload, params }) => ({
        url: '/portfolio/any/taxes/tax_harvesting/',
        method: 'POST',
        body: cleanPayload(payload, portfolioStrategyPayload),
        params,
      }),
      providesTags: [{ type: 'Portfolio', id: 'TaxHarvesting' }],
    }),

    optimizationSettingsObjectives: build.query<
      OptimizationObjective[],
      {
        assetType?: string;
      }
    >({
      query: /* istanbul ignore next */ ({ assetType = 'stock' }) => ({
        url: `/portfolio/${assetType}/optimization/settings/objectives/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Portfolio', id: 'Objectives' }],
    }),

    rebalanceDeposit: build.query<
      TheoreticalTrade[],
      { params: DepositAmount; payload: PortfolioRebalanceStrategy }
    >({
      query: ({ payload, params }) => ({
        url: '/portfolio/any/rebalance/deposit/',
        method: 'POST',
        body: cleanPayload(payload, rebalanceDepositPayload),
        params,
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceDeposit' }],
    }),

    rebalanceWithdrawals: build.query<
      TheoreticalTrade[],
      { payload: PortfolioRebalanceStrategy }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/any/rebalance/withdrawals/',
        method: 'POST',
        body: cleanPayload(payload, rebalanceWithdrawalsPayload),
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceWithdrawals' }],
    }),

    rebalanceTrading: build.query<
      TheoreticalTrade[],
      { payload: PortfolioRebalanceTrading }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/any/rebalance/trading/',
        method: 'POST',
        body: cleanPayload(payload, rebalanceTradingPayload),
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceTrading' }],
    }),

    rebalanceGeneral: build.query<
      RebalanceTrade[],
      { payload: PortfolioRebalanceGeneral }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/any/rebalance/general/',
        method: 'POST',
        body: cleanPayload(payload, rebalanceGeneralPayload),
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceGeneral' }],
    }),

    rebalanceReportingTarget: build.query<
      RebalanceReportingTargetResp,
      { payload: RebalanceReportingTarget }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/any/rebalance/reporting/target/',
        method: 'POST',
        body: cleanPayload(payload, reportingTargetPayload),
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceReportingTarget' }],
    }),

    rebalanceReportingTaxes: build.query<
      RebalanceReportingTaxesResp,
      { payload: RebalanceReportingTaxes }
    >({
      query: ({ payload }) => ({
        url: '/portfolio/any/rebalance/reporting/taxes/',
        method: 'POST',
        body: cleanPayload(payload, reportingTaxesPayload),
      }),
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Portfolio', id: 'RebalanceReportingTaxes' }],
    }),
  }),
});
